import { DateTime } from 'luxon'
import { computed, type Ref } from 'vue'
import { isDateTimeAfter, isDateTimeBefore, now } from '@/common/dateUtils'
import {
	getDuration,
	getRangeDescription,
	hasNonOptimalDepth,
	hasNonOptimalLength,
	isDiscountEvent,
	isNonOptimalEvent,
	isOpportunityEvent
} from '@/common/utils'
import type { EventData } from '@/types'

export function useEvent(event: Ref<EventData>) {
	const duration = computed(() => getDuration(event.value.start_date, event.value.end_date))
	const recommendedDuration = computed(() =>
		event.value.recommended_start_date && event.value.recommended_end_date
			? getDuration(event.value.recommended_start_date, event.value.recommended_end_date)
			: null
	)
	const rangeDescription = computed(() => getRangeDescription(event.value.start_date, event.value.end_date))
	const recommendedRangeDescription = computed(() =>
		!!event.value.recommended_start_date && !!event.value.recommended_end_date
			? getRangeDescription(event.value.recommended_start_date, event.value.recommended_end_date)
			: ''
	)
	const isLengthNotOptimal = computed(() => {
		return hasNonOptimalLength(event.value)
	})
	const isDepthNotOptimal = computed(() => {
		return hasNonOptimalDepth(event.value)
	})
	const isNonOptimal = computed(() => isNonOptimalEvent(event.value))
	const hasStarted = computed(() => event.value.start_date < now())
	const hasEnded = computed(() => event.value.end_date < now())
	const isClickable = computed(() => {
		return isDiscountEvent(event.value) || (!hasStarted.value && isOpportunityEvent(event.value))
	})
	const isEditable = computed(() => {
		return isDiscountEvent(event.value) && !hasStarted.value && !!event.value.id
	})
	const isFromCalculatedOpportunity = computed(() => event.value.opportunity_origin?.opportunity_type === 'calculated')
	const isOpportunity = computed(() => isOpportunityEvent(event.value))
	const isFromStoreOpportunity = computed(
		() =>
			// Planned, non-scraped events
			event.value.opportunity_origin?.opportunity_type === 'store' ||
			// Events already added to store backend
			event.value?.discount_event_type === 'store_discount'
	)

	const isPossiblyNotLegalNOAEvent = computed(() => {
		const newRulesDate = DateTime.fromISO('2025-06-23T00:00:00.000Z')
		const newRulesAvailableInEventsPlanner = DateTime.fromISO('2025-07-01T00:00:00.000Z')

		return (
			event.value.create_time?.isValid &&
			event.value.end_date.isValid &&
			newRulesDate.isValid &&
			newRulesAvailableInEventsPlanner.isValid &&
			event.value.store_name === 'nintendo_switch_americas' &&
			isDateTimeBefore(event.value.create_time, newRulesAvailableInEventsPlanner) &&
			isDateTimeAfter(event.value.end_date, newRulesDate)
		)
	})

	return {
		duration,
		recommendedDuration,
		rangeDescription,
		recommendedRangeDescription,
		isLengthNotOptimal,
		isDepthNotOptimal,
		isNonOptimal,
		hasEnded,
		isClickable,
		isEditable,
		isFromCalculatedOpportunity,
		isFromStoreOpportunity,
		isOpportunity,
		isPossiblyNotLegalNOAEvent
	}
}
